import {
  DefaultError,
  infiniteQueryOptions,
  QueryFunctionContext,
  UndefinedInitialDataInfiniteOptions,
} from "@tanstack/react-query";
import { QueryKeyParams, QueryError, TalaeraQueryKey } from ".";

type PaginationParams = {
  page: number;
  size: number;
};

export type PaginatedResponse<TEntity> = {
  list: TEntity[];
  total_count: number;
};

/**
 * Shared config for Talaera infinite query factories.
 *
 * `path` is both the stable cache key prefix and the request path passed to the
 * fetcher. `queryFn` receives the caller's query params merged with the current
 * page params (`page`, `size`), so paginated endpoints can keep one query shape.
 */
type InfiniteApiQueryProps<TEntity, TQueryParams extends QueryKeyParams, TPayload> = {
  path: string;
  queryFn: (
    path: string,
    queryParams: TQueryParams & PaginationParams,
    payload: TPayload | undefined,
    context: QueryFunctionContext<TalaeraQueryKey<TQueryParams, TPayload>>
  ) => Promise<PaginatedResponse<TEntity>>;
} & RemainingQueryOptions<TEntity, TQueryParams, TPayload>;

/**
 * Creates an infinite-query factory with Talaera's standard `[path, params, payload]`
 * cache key shape.
 *
 * The returned query flattens `pages[].list` into a single `TEntity[]`, starts at
 * page 1, and uses `total_count` to decide whether another page exists. `mainKey`
 * is the `[path]` prefix for invalidating every variant of the infinite query.
 */
export function infiniteApiQuery<
  TEntity,
  TQueryParams extends QueryKeyParams,
  TPayload = void,
>(config: InfiniteApiQueryProps<TEntity, TQueryParams, TPayload>) {
  const { path, queryFn, ...restConfig } = config;

  function createQueryOptions(
    pageSize = DEFAULT_PAGE_SIZE,
    queryParams: TQueryParams,
    payload: TPayload
  ) {
    return infiniteQueryOptions({
      queryKey: [path, queryParams, payload],

      queryFn: context => {
        const paginatedParams = {
          ...queryParams,
          ...context.pageParam,
        } as TQueryParams & PaginationParams;
        return queryFn(path, paginatedParams, payload, context).catch(
          (error: unknown) => {
            throw new QueryError(error);
          }
        );
      },

      select: data => data.pages.map(page => page.list).flat(),

      initialPageParam: {
        page: 1,
        size: pageSize,
      },

      getNextPageParam: (lastPage, _, lastPageParam) => {
        return lastPageParam.page * pageSize < lastPage.total_count
          ? {
              page: lastPageParam.page + 1,
              size: pageSize,
            }
          : null;
      },

      ...restConfig,
    });
  }

  createQueryOptions.mainKey = [config.path];

  return createQueryOptions as typeof createQueryOptions & {
    mainKey: [string];
  };
}

const DEFAULT_PAGE_SIZE = 10;

type RemainingQueryOptions<TEntity, TQueryParams extends QueryKeyParams, TPayload> = Omit<
  UndefinedInitialDataInfiniteOptions<
    PaginatedResponse<TEntity>,
    DefaultError,
    TEntity[],
    TalaeraQueryKey<TQueryParams, TPayload>,
    PaginationParams
  >,
  "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
>;
